import { allMoves } from "./moves";
import { BP_FINISHER, TEMPLATES } from "./templates";
import type { ExerciseId } from "./types";

/**
 * What the player can say (ADR-055): a fixed vocabulary of about twenty words and a walk's five cues (ADR-063), plus every move by
 * its id. A cue is a short list of these — "next" then "sitstand" — played one after another, so
 * the owner records each word once and never a sentence.
 *
 * A clip is `/voice/<lang>/<id>.ogg`: Ogg Opus, which Chrome decodes with its own bundled decoder
 * on any Android, Google services or not, where AAC goes through the phone's platform codecs.
 * `scripts/voice-manifest.mjs` writes `public/voice/manifest.json` from `voiceManifest()`, so the
 * owner has the list to record from; the test holds the committed file to this function.
 */

export type VoiceLang = "en" | "hi";

export interface VoiceWord {
  id: string;
  en: string;
  hi: string;
}

export const VOICE_WORDS: readonly VoiceWord[] = [
  { id: "next", en: "Next", hi: "अगला" },
  { id: "rest", en: "Rest", hi: "आराम" },
  { id: "switch-sides", en: "Switch sides", hi: "अब दूसरी तरफ़" },
  { id: "stand-up-slowly", en: "Stand up slowly", hi: "धीरे से खड़े हों" },
  { id: "breathe", en: "Breathe", hi: "साँस लें" },
  { id: "last-one", en: "Last one", hi: "आख़िरी" },
  { id: "halfway", en: "Halfway", hi: "आधा हो गया" },
  { id: "well-done", en: "Well done", hi: "शाबाश" },
  { id: "stop-if-it-hurts", en: "Stop if it hurts", hi: "दर्द हो तो रुक जाएँ" },
  { id: "left", en: "Left", hi: "बायाँ" },
  { id: "right", en: "Right", hi: "दायाँ" },
  { id: "1", en: "One", hi: "एक" },
  { id: "2", en: "Two", hi: "दो" },
  { id: "3", en: "Three", hi: "तीन" },
  { id: "4", en: "Four", hi: "चार" },
  { id: "5", en: "Five", hi: "पाँच" },
  { id: "6", en: "Six", hi: "छह" },
  { id: "7", en: "Seven", hi: "सात" },
  { id: "8", en: "Eight", hi: "आठ" },
  { id: "9", en: "Nine", hi: "नौ" },
  { id: "10", en: "Ten", hi: "दस" },
  // a walk's fixed cues (ADR-063): whole sentences, because each is said alone
  { id: "brisk", en: "Brisk now. Breathing harder, still able to talk.", hi: "अब तेज़ चलें। साँस तेज़ हो, पर बात कर सकें।" },
  { id: "easy", en: "Easy now. Catch your breath.", hi: "अब धीरे चलें। साँस सामान्य होने दें।" },
  { id: "turn-back", en: "Half-way. Turn back now.", hi: "आधा हो गया। अब वापस मुड़ें।" },
  { id: "planned-done", en: "That is your planned walk. Finish when you like.", hi: "आपकी तय सैर पूरी हुई। जब चाहें, ख़त्म करें।" },
  { id: "rounds-done", en: "Rounds done. Walk home easy.", hi: "राउंड पूरे हुए। अब आराम से घर चलें।" },
];

const WORDS = new Map(VOICE_WORDS.map((w) => [w.id, w]));

/** A fixed word, or null for an id that is not one (a move id, say). */
export function voiceWord(id: string): VoiceWord | null {
  return WORDS.get(id) ?? null;
}

export const VOICE_FORMAT = "ogg";

/** The clip's path under the site root: `voice/hi/next.ogg`. */
export function clipPath(lang: VoiceLang, id: string): string {
  return `voice/${lang}/${encodeURIComponent(id)}.${VOICE_FORMAT}`;
}

/**
 * What one part of a cue says in words, for the phone's own voice when there is no clip: the
 * word's text, or the move's name (its Hindi name in Hindi, when it has one). Null for an id that
 * is neither.
 */
export function voiceText(lang: VoiceLang, id: string): string | null {
  const w = voiceWord(id);
  if (w) return w[lang];
  const ex = allMoves().find((m) => m.id === id);
  if (!ex) return null;
  return lang === "hi" && ex.hi ? ex.hi : ex.n;
}

/** The moves the Steady templates name: the ones a parent hears first, so recorded first. */
export function firstMoves(): Set<ExerciseId> {
  const out = new Set<ExerciseId>();
  for (const t of Object.values(TEMPLATES)) for (const b of t.blocks) for (const s of b.items) {
    out.add(s.move);
    if (s.steady) out.add(s.steady);
  }
  for (const s of BP_FINISHER.items) out.add(s.move);
  return out;
}

export interface VoiceManifestRow {
  id: string;
  /** the file name, the same in both language folders */
  file: string;
  en: string;
  /** null where a move has no Hindi name yet: record the English one in both folders */
  hi: string | null;
  /** a Steady move: record these first */
  first?: true;
}

export interface VoiceManifest {
  format: string;
  codec: string;
  sampleRate: number;
  channels: number;
  loudness: string;
  langs: VoiceLang[];
  words: VoiceManifestRow[];
  moves: VoiceManifestRow[];
}

export function voiceManifest(): VoiceManifest {
  const first = firstMoves();
  return {
    format: VOICE_FORMAT,
    codec: "opus",
    sampleRate: 44100,
    channels: 1,
    loudness: "-16 LUFS",
    langs: ["en", "hi"],
    words: VOICE_WORDS.map((w) => ({ id: w.id, file: `${w.id}.${VOICE_FORMAT}`, en: w.en, hi: w.hi })),
    moves: allMoves().map((m) => ({
      id: m.id,
      file: `${m.id}.${VOICE_FORMAT}`,
      en: m.n,
      hi: m.hi ?? null,
      ...(first.has(m.id) ? { first: true as const } : {}),
    })),
  };
}
